// src/RecipeDB/recipeService.js
import {collection, setDoc, doc, getDocs, updateDoc, arrayUnion, arrayRemove, getDoc, serverTimestamp} from "firebase/firestore"; // Importación de Firebase Firestore
import {getStorage, ref, uploadBytes, getDownloadURL} from "firebase/storage"; // Importar Firebase Storage
import {db} from '../config/firebase';
import {v4 as uuidv4} from 'uuid'; // Importar uuid para generar identificadores únicos

// Función para buscar recetas en Firestore
const searchRecipes = async (searchTerm) => {
    try {
        const recipesCollection = collection(db, "recetas");
        const querySnapshot = await getDocs(recipesCollection);
        
        const recipes = [];
        querySnapshot.forEach((doc) => {
            const data = doc.data();

            // Verificar que el campo 'nombre' esté definido y que el término de búsqueda esté en el nombre
            if (data.nombre && data.nombre.toLowerCase().includes(searchTerm.toLowerCase())) {
                recipes.push({id: doc.id, ...data}); // Agrega la receta si hay coincidencia
           }
       });

        // Priorizar las recetas creadas por los usuarios y de CookyChat
        const sortedRecipes = recipes.sort((a, b) => {
            if (a.fuente === 'CookyChat' || a.fuente === 'usuario') return -1;
            if (b.fuente === 'CookyChat' || b.fuente === 'usuario') return 1;
            return 0;
       });

        return sortedRecipes; // Retorna las recetas coincidentes y ordenadas
   } catch (error) {
        console.error("Error al buscar recetas:", error.message);
        return [];
   }
};

// Función para añadir una receta a Firestore
const addRecipe = async (recipe) => {
    try {
        const recipeId = recipe.id || uuidv4();
        await setDoc(doc(db, "recetas", recipeId), {
            ...recipe,
            id: recipeId,
            tiempoEstimado: recipe.tiempoEstimado ?? recipe.tiempoPreparacion ?? 0,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
        });
    } catch (error) {
        console.error("Error añadiendo receta: ", error.message);
    }
};

// Función para añadir una receta a favoritos en Firestore
const addRecipeToFavorites = async (userId, recipe) => {
    try {
        const userDocRef = doc(db, "users", userId);
        const userDoc = await getDoc(userDocRef);

        if (!userDoc.exists()) {
            // Crear el documento del usuario si no existe
            await setDoc(userDocRef, {recetasFavoritas: []});
       }

        await updateDoc(userDocRef, {
            recetasFavoritas: arrayUnion(recipe)
       });
   } catch (error) {
        console.error("Error añadiendo receta a favoritos:", error.message);
   }
};

// Función para eliminar una receta de favoritos en Firestore
const removeRecipeFromFavorites = async (userId, recipe) => {
    try {
        const userDocRef = doc(db, "users", userId);
        await updateDoc(userDocRef, {
            recetasFavoritas: arrayRemove(recipe)
       });
   } catch (error) {
        console.error("Error eliminando receta de favoritos:", error.message);
   }
};

// Función para obtener todas las recetas de Firestore
const getRecipes = async () => {
    try {
        const recipesCollection = collection(db, "recetas");
        const recipeSnapshot = await getDocs(recipesCollection);
        const recipeList = recipeSnapshot.docs.map(doc => ({id: doc.id, ...doc.data()}));
        return recipeList;
   } catch (error) {
        console.error("Error al obtener las recetas: ", error.message);
        return [];
   }
};

// Función para obtener las recetas favoritas de un usuario
export const getFavoriteRecipes = async (userId) => {
    try {
        const userDocRef = doc(db, "users", userId);
        const userDoc = await getDoc(userDocRef);

        if (userDoc.exists()) {
            return userDoc.data().recetasFavoritas || [];
       } else {
            return [];
       }
   } catch (error) {
        console.error("Error al obtener las recetas favoritas: ", error.message);
        return [];
   }
};

// Función para compartir una receta en el feed
const handleShareRecipe = async (recipe, userId, imageFile = null, onSuccess = null) => {
    if (!userId) return;

    const storage = getStorage();
    const newRecipe = {...recipe, userId, fuente: 'usuario'};

    if (imageFile) {
        const storageRef = ref(storage, `images/${uuidv4()}`);
        await uploadBytes(storageRef, imageFile);
        newRecipe.imagen = await getDownloadURL(storageRef);
   }

    const recipeId = newRecipe.id || newRecipe.nombre.replace(/[^a-zA-Z0-9]/g, '_');

    // 1) Crea/actualiza la receta en la colección pública
    await setDoc(doc(db, 'recetas', recipeId), {
        ...newRecipe,
        id: recipeId,
        userId,
        nombre: newRecipe.nombre,
        ingredientes: newRecipe.ingredientes || [],
        instrucciones: newRecipe.instrucciones || [],
        dificultad: newRecipe.dificultad || 'fácil',
        tiempoEstimado: newRecipe.tiempoEstimado ?? newRecipe.tiempoPreparacion ?? 0,
        fuente: newRecipe.fuente || 'usuario',
        imagen: newRecipe.imagen || '',
        imagenes: newRecipe.imagenes || (newRecipe.imagen ? [newRecipe.imagen] : []), // <- NUEVO
        urlOrigen: newRecipe.urlOrigen || '',
        valoraciones: [],
        likes: [],
        privacidad: newRecipe.privacidad || 'publica', // <- antes: hardcoded 'publica'
        sharedWith: [],
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        sharedAt: serverTimestamp(),
    }, {merge: true});

    // 2) Guarda SOLO el ID en el perfil del usuario
    const userDocRef = doc(db, 'users', userId);
    await updateDoc(userDocRef, {
        recetasCompartidas: arrayUnion(recipeId) // SOLO el ID
   });

    if (onSuccess) onSuccess();
};

// Función para editar una receta en Firestore
export const editRecipe = async (recipeId, updatedRecipe) => {
    try {
        const recipeRef = doc(db, "recetas", recipeId);
        await updateDoc(recipeRef, updatedRecipe);
    } catch (error) {
        console.error("Error editando receta:", error.message);
    }
};

export {
    searchRecipes,
    addRecipe,
    addRecipeToFavorites,
    removeRecipeFromFavorites,
    getRecipes,
    handleShareRecipe
};